import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { ShieldCheck, Info, Check, X, Lock, Star, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Insurance() {
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [region, setRegion] = useState('all');
  const [destination, setDestination] = useState('');
  const [travellers, setTravellers] = useState('1');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    document.title = 'Travel Insurance — Compare Plans | FlightDeals.in';
    api.insuranceMeta().then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const load = () => {
    setData(null);
    api.insurancePlans({ region: region === 'all' ? '' : region, destination })
      .then(({ data }) => setData(data)).catch(() => setData({ plans: [] }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [region]);

  const purchasable = data?.purchasable ?? meta?.purchasable ?? false;

  const openOffer = (plan) => {
    // Central attribution only — never a raw provider URL. Only reachable for APPROVED live offers.
    if (!plan.purchasable || !(plan.offer_ids || []).length) return;
    window.open(api.goVerticalUrl('insurance', plan.offer_ids[0], { surface: 'insurance_compare', source: '/insurance' }), '_blank');
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="bg-gradient-to-b from-accent/10 to-background border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-6 w-6 text-accent" />
            <h1 className="font-display text-3xl font-semibold" data-testid="insurance-title">Travel Insurance</h1>
            <Badge variant="outline" className="ml-1 text-[11px] text-amber-600 border-amber-500/40" data-testid="insurance-preview-badge">Preview</Badge>
          </div>
          <p className="text-muted-foreground max-w-2xl">Compare travel insurance coverage for your trip. Indicative examples for comparison only — not live quotes.</p>

          {/* Trip inputs */}
          <div className="mt-5 grid sm:grid-cols-4 gap-3 max-w-3xl" data-testid="insurance-trip-form">
            <div>
              <Label className="text-xs">Destination</Label>
              <Input value={destination} onChange={(e) => setDestination(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="e.g. Thailand" data-testid="insurance-destination" />
            </div>
            <div>
              <Label className="text-xs">Coverage region</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="insurance-region"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {(meta?.regions || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Travellers</Label>
              <Select value={travellers} onValueChange={setTravellers}>
                <SelectTrigger data-testid="insurance-travellers"><SelectValue /></SelectTrigger>
                <SelectContent>{['1', '2', '3', '4', '5+'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={load} className="w-full rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="insurance-compare-btn">Compare plans</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Compliance gate banner */}
        {!purchasable && (
          <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="insurance-gate-banner">
            <Lock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-amber-700">Not currently available to purchase through FlightDeals.in</p>
              <p className="text-muted-foreground mt-1">{meta?.not_available_message || 'We\u2019re finalising an approved, licensed insurance partner. The plans below are indicative examples for comparison only \u2014 not live quotes.'}</p>
            </div>
          </div>
        )}

        {/* Plans */}
        {data === null ? (
          <div className="grid md:grid-cols-2 gap-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-64 w-full rounded-2xl" />)}</div>
        ) : data.plans.length === 0 ? (
          <Card className="p-12 rounded-2xl text-center" data-testid="insurance-empty">
            <ShieldCheck className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
            <p className="font-semibold">No plans match your filters</p>
            <p className="text-sm text-muted-foreground">Try a different coverage region.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="insurance-plan-list">
            {data.plans.map((p) => {
              const open = expanded[p.id];
              return (
                <Card key={p.id} className="p-5 rounded-2xl flex flex-col" data-testid={`insurance-plan-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-lg font-semibold">{p.plan_name}</h3>
                        {p.featured && <Badge className="text-[10px] bg-accent text-accent-foreground"><Star className="h-3 w-3 mr-0.5" />Popular</Badge>}
                        {p.is_demo && <Badge variant="outline" className="text-[10px] text-amber-600" data-testid={`insurance-demo-${p.id}`}>Indicative</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">Underwritten by {p.insurer} · {p.coverage_region}</p>
                    </div>
                    <div className="text-right shrink-0">
                      {p.premium_from != null && (
                        <>
                          <p className="text-[10px] text-muted-foreground">from (indicative)</p>
                          <p className="text-xl font-semibold tabular-nums">₹{Number(p.premium_from).toLocaleString('en-IN')}</p>
                        </>
                      )}
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mt-3">{p.coverage_limits}</p>

                  {/* Benefits */}
                  <div className="mt-3 space-y-1.5">
                    {(p.benefits || []).slice(0, open ? 99 : 4).map((b, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600 shrink-0" /><span>{b}</span></div>
                    ))}
                  </div>

                  {open && (p.exclusions || []).length > 0 && (
                    <div className="mt-3 space-y-1.5 border-t border-border pt-3" data-testid={`insurance-exclusions-${p.id}`}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Key exclusions</p>
                      {(p.exclusions || []).map((x, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground"><X className="h-4 w-4 text-red-500 shrink-0" /><span>{x}</span></div>
                      ))}
                    </div>
                  )}

                  <button onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))} className="mt-3 text-sm text-accent hover:underline text-left" data-testid={`insurance-toggle-${p.id}`}>
                    {open ? 'Show less' : 'View benefits & exclusions'}
                  </button>

                  <div className="mt-auto pt-4">
                    {p.premium_note && <p className="text-[11px] text-muted-foreground mb-2 flex items-center gap-1"><Info className="h-3 w-3" />{p.premium_note}</p>}
                    {p.purchasable ? (
                      <Button onClick={() => openOffer(p)} className="w-full rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid={`insurance-continue-${p.id}`}>Continue to partner</Button>
                    ) : (
                      <Button disabled className="w-full rounded-xl" data-testid={`insurance-unavailable-${p.id}`}><Lock className="h-4 w-4 mr-1.5" />Not available to purchase yet</Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Legal disclosure */}
        <div className="mt-8 rounded-2xl border border-border bg-secondary/40 p-4 flex items-start gap-3" data-testid="insurance-disclosure">
          <AlertTriangle className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">{data?.disclosure || meta?.disclosure}</p>
        </div>
      </div>
    </Layout>
  );
}
